import { Controller } from "@hotwired/stimulus"
import { createIcons, icons } from 'lucide'
import confetti from 'canvas-confetti'

export default class extends Controller {
  static targets = [
    "form",
    "noteInput",
    "progressBar",
    "progressText",
    "beatSection",
    "subBeatContainer",
    "subBeatChevron",
    "wordCount",
    "autoSaveStatus",
    "feedbackButtonText",
    "expandIcon",
    "expandText",
    "container"
  ]

  connect() {
    this.celebrationTriggered = false
    this.wasComplete = this.isOutlineComplete()
    this.expandedBeatId = null
    this.updateProgress()
    this.updateAllWordCounts()
    setTimeout(() => createIcons({ icons }), 0)
  }

  isOutlineComplete() {
    const inputs = this.noteInputTargets.filter(input => input.dataset.beatId)
    if (inputs.length === 0) return false
    return inputs.every(input => {
      const words = input.value.trim().split(/\s+/).filter(w => w.length > 0).length
      return words >= 20
    })
  }

  launchFireworks() {
    const duration = 4000
    const animationEnd = Date.now() + duration
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 }

    const randomInRange = (min, max) => Math.random() * (max - min) + min

    // Show celebration text
    this.showCelebrationText()

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now()

      if (timeLeft <= 0) {
        return clearInterval(interval)
      }

      const particleCount = 50 * (timeLeft / duration)

      // Launch from two sides
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#22c55e', '#84cc16', '#10b981', '#06b6d4', '#f59e0b']
      })
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#22c55e', '#84cc16', '#10b981', '#06b6d4', '#f59e0b']
      })
    }, 250)
  }

  showCelebrationText() {
    const overlay = document.createElement('div')
    overlay.className = 'celebration-overlay'
    overlay.innerHTML = `
      <div class="celebration-text">
        <span class="celebration-title">Outline Complete!</span>
        <span class="celebration-subtitle">Great work on your story</span>
      </div>
    `

    const style = document.createElement('style')
    style.textContent = `
      .celebration-overlay {
        position: fixed;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9998;
        pointer-events: none;
        animation: overlayFade 4s ease-out forwards;
      }
      .celebration-text {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;
        animation: textBounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
      }
      .celebration-title {
        font-size: 4rem;
        font-weight: 800;
        background: linear-gradient(135deg, #22c55e 0%, #84cc16 50%, #10b981 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        text-shadow: 0 4px 30px rgba(34, 197, 94, 0.4);
        filter: drop-shadow(0 2px 10px rgba(0,0,0,0.1));
      }
      .celebration-subtitle {
        font-size: 1.5rem;
        font-weight: 500;
        color: #6b7280;
      }
      @keyframes textBounce {
        0% { transform: scale(0.3); opacity: 0; }
        50% { transform: scale(1.1); }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes overlayFade {
        0%, 60% { opacity: 1; }
        100% { opacity: 0; }
      }
      @media (max-width: 640px) {
        .celebration-title { font-size: 2.5rem; }
        .celebration-subtitle { font-size: 1rem; }
      }
    `

    document.head.appendChild(style)
    document.body.appendChild(overlay)

    // Remove elements after animation
    setTimeout(() => {
      overlay.remove()
      style.remove()
    }, 4000)
  }

  updateProgress() {
    const inputs = this.noteInputTargets.filter(input => input.dataset.beatId)
    const completedInputs = inputs.filter(input => {
      const words = input.value.trim().split(/\s+/).filter(w => w.length > 0).length
      return words >= 20
    })
    const progress = inputs.length > 0 ? (completedInputs.length / inputs.length) * 100 : 0

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${progress}%`
    }
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${Math.round(progress)}%`
    }

    // Check for completion and trigger celebration
    const isNowComplete = this.isOutlineComplete()
    if (isNowComplete && !this.wasComplete && !this.celebrationTriggered) {
      this.celebrationTriggered = true
      this.launchFireworks()
    }
    this.wasComplete = isNowComplete

    // Update checkmarks on beat titles
    this.beatSectionTargets.forEach(section => {
      const beatId = section.id.replace('beat-', '')
      const input = this.noteInputTargets.find(i => i.dataset.beatId === beatId)
      if (input) {
        const words = input.value.trim().split(/\s+/).filter(w => w.length > 0).length
        const isComplete = words >= 20
        const titleContainer = section.querySelector('.inline-flex')

        if (titleContainer) {
          const existingCheck = titleContainer.querySelector('[data-lucide="check-circle-2"]')
          if (isComplete && !existingCheck) {
            const checkIcon = document.createElement('i')
            checkIcon.className = 'w-6 h-6 text-lime-500'
            checkIcon.setAttribute('data-lucide', 'check-circle-2')
            titleContainer.insertBefore(checkIcon, titleContainer.firstChild)
            createIcons({ icons })
          } else if (!isComplete && existingCheck) {
            existingCheck.remove()
          }
        }
      }
    })
  }

  updateAllWordCounts() {
    this.noteInputTargets.forEach(input => {
      if (input.dataset.beatId) {
        this.updateWordCountFor(input)
      }
    })
  }

  updateWordCount(event) {
    this.updateWordCountFor(event.target)
  }

  updateWordCountFor(input) {
    const beatId = input.dataset.beatId
    if (!beatId) return

    const words = input.value.trim().split(/\s+/).filter(w => w.length > 0).length

    const wordCountEl = this.wordCountTargets.find(w => w.dataset.beatId === beatId)
    if (wordCountEl) {
      wordCountEl.textContent = `(${words})`
    }

    // Update feedback button text based on word count
    const feedbackBtnText = this.feedbackButtonTextTargets.find(b => b.dataset.beatId === beatId)
    if (feedbackBtnText) {
      feedbackBtnText.textContent = words >= 5 ? "Get Feedback" : "How do I start?"
    }
  }

  autoSave() {
    clearTimeout(this.autoSaveTimeout)

    if (this.hasAutoSaveStatusTarget) {
      this.autoSaveStatusTarget.textContent = "Saving..."
    }

    this.autoSaveTimeout = setTimeout(() => {
      this.save()
    }, 1500)
  }

  save() {
    if (!this.hasFormTarget) return

    const formData = new FormData(this.formTarget)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(this.formTarget.action, {
      method: 'PATCH',
      body: formData,
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken,
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        if (this.hasAutoSaveStatusTarget) {
          this.autoSaveStatusTarget.textContent = "Saved"
          setTimeout(() => {
            this.autoSaveStatusTarget.textContent = ""
          }, 2000)
        }
      }
    })
    .catch(error => {
      console.error('Save failed:', error)
      if (this.hasAutoSaveStatusTarget) {
        this.autoSaveStatusTarget.textContent = "Save failed"
      }
    })
  }

  toggleSubBeats(event) {
    const beatId = event.currentTarget.dataset.beatId
    const container = this.subBeatContainerTargets.find(c => c.dataset.beatId === beatId)
    const chevron = event.currentTarget.querySelector('[data-lucide="chevron-right"]')

    if (container) {
      container.classList.toggle('hidden')

      if (chevron) {
        if (container.classList.contains('hidden')) {
          chevron.style.transform = 'rotate(0deg)'
        } else {
          chevron.style.transform = 'rotate(90deg)'
        }
      }
    }
  }

  exportOutline() {
    const sections = []

    this.beatSectionTargets.forEach(section => {
      const beatId = section.id.replace('beat-', '')
      const input = this.noteInputTargets.find(i => i.dataset.beatId === beatId)
      const beatTitle = section.querySelector('h2')?.textContent || ''

      if (input && input.value.trim()) {
        sections.push({
          title: beatTitle,
          content: input.value.trim()
        })
      }
    })

    if (sections.length === 0) {
      alert('No notes to export. Please write some notes first.')
      return
    }

    let exportText = "STORY OUTLINE\n"
    exportText += "=".repeat(40) + "\n\n"

    sections.forEach((section) => {
      exportText += `${section.title}\n`
      exportText += "-".repeat(30) + "\n"
      exportText += section.content + "\n\n"
    })

    const blob = new Blob([exportText], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'story-outline.txt'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  toggleExpand(event) {
    const beatId = event.currentTarget.dataset.beatId
    const isCurrentlyExpanded = this.expandedBeatId === beatId

    if (isCurrentlyExpanded) {
      // Collapse: show all beats
      this.expandedBeatId = null

      this.beatSectionTargets.forEach(section => {
        section.classList.remove("hidden", "expanded-beat")
      })

      // Update all expand buttons
      this.expandIconTargets.forEach(icon => {
        icon.setAttribute("data-lucide", "maximize-2")
      })
      this.expandTextTargets.forEach(text => {
        text.textContent = "Expand"
      })
    } else {
      // Expand: hide other beats, expand this one
      this.expandedBeatId = beatId

      this.beatSectionTargets.forEach(section => {
        const sectionBeatId = section.id.replace("beat-", "")
        if (sectionBeatId === beatId) {
          section.classList.remove("hidden")
          section.classList.add("expanded-beat")
        } else {
          section.classList.add("hidden")
          section.classList.remove("expanded-beat")
        }
      })

      // Update expand buttons
      this.expandIconTargets.forEach(icon => {
        if (icon.dataset.beatId === beatId) {
          icon.setAttribute("data-lucide", "minimize-2")
        } else {
          icon.setAttribute("data-lucide", "maximize-2")
        }
      })
      this.expandTextTargets.forEach(text => {
        if (text.dataset.beatId === beatId) {
          text.textContent = "Collapse"
        } else {
          text.textContent = "Expand"
        }
      })

      // Scroll to top
      if (this.hasContainerTarget) {
        this.containerTarget.scrollTop = 0
      }
    }

    // Refresh Lucide icons
    createIcons({ icons })
  }
}
